
// src/pages/checagens/ChecagemDetailPage.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChecagem, useChecagemImoveis, useFinalizarChecagem, useCreateChecagemCampanha } from '../../hooks/useChecagens';
import Loading from '../../components/Loading';
import ErrorToast from '../../components/ErrorToast';
import Toast from '../../components/Toast';
import ChecagemKPI from '../../components/checagens/ChecagemKPI';
import ChecagemImoveisTable from '../../components/checagens/ChecagemImoveisTable';
import ChecagemImoveisModal from '../../components/checagens/ChecagemImoveisModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import Paginacao from '../../components/Paginacao';
import './ChecagemDetailPage.css';

const ChecagemDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const checagemId = Number(id);
    const navigate = useNavigate();
    const [isAddImovelModalOpen, setAddImovelModalOpen] = useState(false);
    const [isFinalizarModalOpen, setFinalizarModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const pageSize = 10;
    const [toastMsg, setToastMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const { data: checagem, isLoading: isLoadingChecagem, error: errorChecagem } = useChecagem(checagemId);
    const { data: imoveisResult, isLoading: isLoadingImoveis, error: errorImoveis } = useChecagemImoveis(checagemId, { page: currentPage, pageSize, status_workflow: statusFilter });
    const finalizarChecagemMutation = useFinalizarChecagem();
    const createCampanhaMutation = useCreateChecagemCampanha();

    const handleFinalizar = () => {
        finalizarChecagemMutation.mutate(checagemId, {
            onSuccess: () => {
                setFinalizarModalOpen(false);
            }
        });
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleCriarCampanha = (canal: 'email' | 'whatsapp') => {
        setErrorMsg('');
        createCampanhaMutation.mutate(
            { id: checagemId, data: { canal } },
            {
                onSuccess: () => {
                    setToastMsg(`Campanha (${canal}) criada com sucesso!`);
                },
                onError: (e: any) => {
                    const msg = e?.response?.data?.error || 'Erro ao criar campanha da checagem.';
                    setErrorMsg(msg);
                }
            }
        );
    };

    if (isLoadingChecagem || isLoadingImoveis) return <Loading />;
    if (errorChecagem || errorImoveis) return <ErrorToast message={errorChecagem?.message || errorImoveis?.message} onClose={() => {}} />;

    const imoveis = imoveisResult?.data || [];
    const totalImoveis = imoveisResult?.total || 0;

    return (
        <div className="page-container">
            {errorMsg && <ErrorToast message={errorMsg} onClose={() => setErrorMsg('')} />}
            {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg('')} />}
            <div className="page-header">
                <div className="header-left">
                    <button onClick={() => navigate('/checagens')} className="button-secondary">Voltar</button>
                </div>
                <div className="header-center">
                    <h1 className="page-title">{checagem?.nome || `Checagem #${checagemId}`}</h1>
                </div>
                <div className="header-right">
                    <button onClick={() => setAddImovelModalOpen(true)} className="button">Adicionar Imóvel</button>
                    {checagem?.status !== 'finalizada' && 
                        <button onClick={() => setFinalizarModalOpen(true)} className="button">Finalizar Checagem</button>
                    }
                    <button
                        onClick={() => handleCriarCampanha('email')}
                        className="button"
                        disabled={createCampanhaMutation.isPending}
                    >
                        Criar campanha e-mail
                    </button>
                    <button
                        onClick={() => handleCriarCampanha('whatsapp')}
                        className="button"
                        disabled={createCampanhaMutation.isPending}
                    >
                        Criar campanha whatsapp
                    </button>
                </div>
            </div>

            <div className="details-grid">
                <div className="card">
                    <h4>Progresso Geral</h4>
                    <ChecagemKPI checagemId={checagemId} />
                </div>
                <div className="card">
                    <h4>Responsável</h4>
                    <p>{checagem?.responsavel_usuario_nome || 'Não atribuído'}</p>
                </div>
                <div className="card">
                    <h4>Imóveis na Checagem</h4>
                    <p>{totalImoveis}</p>
                </div>
            </div>

            <div className="filter-container">
                <select value={statusFilter} onChange={handleStatusChange}>
                    <option value="">Todos os Status</option>
                    <option value="pendente">Pendente</option>
                    <option value="andamento">Em Andamento</option>
                    <option value="finalizado">Finalizado</option>
                </select>
            </div>

            {imoveis && <ChecagemImoveisTable imoveis={imoveis} />}
            <Paginacao 
                paginaAtual={currentPage}
                totalItens={totalImoveis}
                itensPorPagina={pageSize}
                setPaginaAtual={handlePageChange}
            />

            {isAddImovelModalOpen && (
                <ChecagemImoveisModal 
                    checagemId={checagemId} 
                    onClose={() => setAddImovelModalOpen(false)} 
                    onFinish={() => {}}
                />
            )}

            {isFinalizarModalOpen && (
                <ConfirmDialog
                    message="Tem certeza que deseja finalizar esta checagem?"
                    onConfirm={handleFinalizar}
                    onCancel={() => setFinalizarModalOpen(false)}
                />
            )}
        </div>
    );
};

export default ChecagemDetailPage;
